import { useState, useRef } from "react"
import { motion } from "motion/react"
import { usePurchase } from "../../../context/purchaseContext"
import { useAppState } from "../../../context/appStateContext"
import useStrings from "../../../localization/useStrings"
import { formatCurrency, formatProductPrice } from "../../../utils/utils"
import native from "../../../native/native"
import svg from "../../../utils/svg"
import useQueryParams from "../../../hooks/useQueryParams"
import useHasPro from "../../../hooks/useHasPro"
import useSwitchToBasicShown from "../../../hooks/useSwitchToBasicShown"
import { useThreeStepStrings } from "../useThreeStepExpirationStrings"
import { CheckIcon, ImageIcon } from "../icons"
import PlanCard from "../PlanCard/PlanCard"
import RedLight from "../RedLight/RedLight"
import styles from "./StepThree.module.scss"

type PlanType = "yearly" | "monthly" | "lifetime"

interface StepThreeProps {
  onBack: () => void
  onLifetimeClick?: () => void
}

const StepThree = ({ onBack, onLifetimeClick }: StepThreeProps) => {
  const [selectedPlan, setSelectedPlan] = useState<PlanType>("yearly")
  const strings = useStrings()
  const expirationStrings = useThreeStepStrings()
  const { state: purchaseState, actions: purchaseActions } = usePurchase()
  const { state: appState } = useAppState()
  const { showLifetime } = useQueryParams()
  const hasPro = useHasPro()
  const isUltimate = purchaseState.ultimateSelected
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const switchToBasicRef = useRef<HTMLButtonElement>(null)

  const plan = purchaseState.selectedPlan
  const yearlyProduct = plan.yearlyProduct
  const monthlyProduct = plan.monthlyProduct
  const yearlyPrice = formatProductPrice(yearlyProduct)
  const monthlyPrice = formatProductPrice(monthlyProduct)
  const monthlyFromYearly = formatCurrency(
    yearlyProduct.price / 12,
    yearlyProduct.priceLocale,
  )
  const savings = Math.round(
    (1 - yearlyProduct.price / 12 / monthlyProduct.price) * 100,
  )

  const lifetimeMultiplier = isUltimate ? 3.333333 : 5
  const lifetimePrice = formatCurrency(
    Math.round(yearlyProduct.price * lifetimeMultiplier + 0.01) - 0.01,
    yearlyProduct.priceLocale,
  )

  const handlePlanClick = (plan: PlanType) => {
    if (plan === "lifetime") {
      onLifetimeClick?.()
    } else {
      setSelectedPlan(plan)
    }
  }

  const handleSubscribe = () => {
    if (selectedPlan === "lifetime") {
      onLifetimeClick?.()
      return
    }
    const product = selectedPlan === "yearly" ? yearlyProduct : monthlyProduct
    native.logMixpanelEvent("html_three_step_subscribe", {
      plan: selectedPlan,
    })
    purchaseActions.purchaseProduct(product)
  }

  const handleSwitchToBasic = () => {
    native.postNativeEvent("close")
  }

  const restoredFeatures = [
    {
      Icon: svg.featureIcons.Star,
      label: isUltimate
        ? strings.threeStepExpiration.allUltimateFeatures
        : strings.threeStepExpiration.allPremiumFeatures,
    },
    {
      Icon: svg.featureIcons.CloudVault,
      label: isUltimate
        ? strings.bubbleExpiration.cloudVaultUltimate
        : strings.bubbleExpiration.cloudVaultGB,
    },
    {
      Icon: svg.featureIcons.SpaceSaver,
      label: strings.threeStepExpiration.spaceSaver,
    },
    { Icon: svg.featureIcons.Trash, label: strings.trashRecovery },
    ...(!hasPro
      ? [
          { Icon: svg.featureIcons.Adfree, label: strings.adFree },
          { Icon: svg.featureIcons.Decoy, label: strings.decoy },
        ]
      : []),
  ]

  const lostInBasic = [
    isUltimate
      ? strings.threeStepExpiration.allUltimateFeatures
      : strings.threeStepExpiration.allPremiumFeatures,
    isUltimate
      ? strings.bubbleExpiration.cloudVaultUltimate
      : strings.bubbleExpiration.cloudVaultGB,
    strings.threeStepExpiration.spaceSaver,
    strings.trashRecovery,
    ...(!hasPro ? [strings.adFree, strings.decoy] : []),
  ]

  useSwitchToBasicShown(scrollContainerRef, switchToBasicRef)

  return (
    <div
      className={`${styles.container} ${showLifetime ? styles.hasLifetime : ""}`}
    >
      {/* Scrollable content */}
      <div ref={scrollContainerRef} className={styles.scrollContainer}>
        {/* Shield icon */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{
            type: "spring",
            stiffness: 200,
            damping: 15,
            delay: 0.2,
          }}
          className={styles.shieldWrapper}
        >
          <div
            className={styles.shieldCircle}
            style={isUltimate ? { background: "#7611a0" } : undefined}
          >
            {isUltimate ? (
              <svg.UltimateIcon className={styles.shieldIcon} />
            ) : (
              <svg.PremiumIcon className={styles.shieldIcon} />
            )}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className={styles.titleWrapper}
        >
          <h1 className={styles.title}>
            {strings.threeStepExpiration.chooseYourPlan}
          </h1>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className={styles.subtitle}
        >
          {isUltimate
            ? strings.threeStepExpiration.restoreUltimateInstantly
            : strings.threeStepExpiration.restorePremiumInstantly}
        </motion.p>

        {/* Plan options */}
        <div className={styles.planList}>
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
          >
            <PlanCard
              duration={strings.threeStepExpiration.twelveMonths}
              price={yearlyPrice}
              perMonthValue={monthlyFromYearly}
              savings={strings.save(savings)}
              popular={true}
              selected={selectedPlan === "yearly"}
              isUltimate={isUltimate}
              onSelect={() => handlePlanClick("yearly")}
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.53 }}
          >
            <PlanCard
              duration={strings.threeStepExpiration.oneMonth}
              price={monthlyPrice}
              perMonthValue={monthlyPrice}
              savings={null}
              popular={false}
              selected={selectedPlan === "monthly"}
              isUltimate={isUltimate}
              onSelect={() => handlePlanClick("monthly")}
            />
          </motion.div>
          {showLifetime && (
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.61 }}
            >
              <PlanCard
                duration={strings.bubbleExpiration.lifetime}
                price={lifetimePrice}
                perMonthValue={lifetimePrice}
                savings={null}
                popular={false}
                selected={selectedPlan === "lifetime"}
                isUltimate={isUltimate}
                isLifetime={true}
                onSelect={() => handlePlanClick("lifetime")}
              />
            </motion.div>
          )}
        </div>

        {/* Restored features */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.75 }}
          className={styles.restoredSection}
        >
          <p className={styles.restoredLabel}>
            {strings.threeStepExpiration.instantlyRestored}
          </p>
          <div className={styles.restoredGrid}>
            {restoredFeatures.map((f, idx) => (
              <div key={idx} className={styles.restoredItem}>
                <CheckIcon className={styles.checkIcon} />
                <span className={styles.restoredText}>{f.label}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className={styles.ctaSection}
        >
          {/* Reassurance banner */}
          <div className={styles.reassuranceBanner}>
            <CheckIcon className={styles.reassuranceCheck} />
            <p className={styles.reassuranceText}>
              {strings.threeStepExpiration.reassurance}
            </p>
          </div>

          <button
            onClick={handleSubscribe}
            className={styles.ctaButton}
            style={
              isUltimate
                ? {
                    background:
                      "linear-gradient(to right, var(--ultimate-gradient-start), var(--ultimate-gradient-end))",
                  }
                : undefined
            }
          >
            {strings.threeStepExpiration.subscribeAndRestore}
          </button>

          <div className={styles.footerLinks}>
            <button
              className={styles.footerLink}
              onClick={() => native.postNativeEvent("terms")}
            >
              {strings.bubbleExpiration.termsOfService}
            </button>
            <button
              className={styles.footerLink}
              onClick={() => native.postNativeEvent("privacy")}
            >
              {strings.bubbleExpiration.privacy}
            </button>
            <button
              className={styles.footerLink}
              onClick={() => native.postNativeEvent("already_purchased")}
            >
              {strings.bubbleExpiration.restorePurchases}
            </button>
          </div>
        </motion.div>

        {/* Switch to Basic section */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1 }}
          className={styles.basicSection}
        >
          <div className={styles.basicHeader}>
            <ImageIcon className={styles.basicHeaderIcon} />
            <h3 className={styles.basicHeaderTitle}>
              {expirationStrings.basicSection.title}
            </h3>
            <span className={styles.basicBadge}>
              {expirationStrings.basicSection.badge}
            </span>
          </div>

          <div className={styles.basicCard}>
            <p className={styles.basicDescription}>
              {expirationStrings.basicSection.description}
            </p>
          </div>

          <button
            ref={switchToBasicRef}
            onClick={handleSwitchToBasic}
            className={styles.switchToBasicButton}
          >
            {expirationStrings.basicSection.cta}
          </button>
        </motion.div>

        <div
          className={styles.bottomInset}
          style={{ height: appState.initialData?.originalBottomInset ?? 0 }}
        />
      </div>
    </div>
  )
}

export default StepThree
